import React, { JSX, useEffect, useState } from 'react'
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { ColorInput } from 'components/ColorPickers'
import { FontSelect } from 'components/FontSelect'
import { FontWeightField } from 'components/FontWeightField'
import { ImageUpload } from 'components/ImageUpload'
import { NumberInput } from 'components/NumberInput'
import { FieldLabel, SectionCard } from 'components/chatOverlays/ChatOverlayManager'
import { LogoNaturalSize, TICKER_BAR_MARQUEE_WIDTH, tickerBarLogoBoxOf } from 'components/TickerBarMarquee'
import { effectiveChatFontWeight } from 'lib/chatFonts'
import {
  MAX_TICKER_BAR_LABEL,
  MAX_TICKER_BAR_MESSAGES,
  MAX_TICKER_BAR_TEXT,
  TICKER_BAR_BAR_BOUNDS,
  TICKER_BAR_COPY,
  TICKER_BAR_LABEL_COLOR_PICKS,
  TICKER_BAR_SHARED_BOUNDS,
  TickerBarDraftProblem,
  TickerBarMessageDraft,
  TickerBarNumberBound,
  clampToBound,
  clipToUnits,
  emptyTickerBarMessage,
  isHex6Color,
  tickerBarTextTallerThanRow,
  unitsLeft
} from 'lib/tickerBarConfig'
import type { TickerBarBarStyle, TickerBarStyle } from 'types/api'

const WEIGHT_DEFAULT = 400

export const tickerBarInputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60'

export const TICKER_BAR_CARD_COPY = Object.freeze({
  topTitle: 'Top bar',
  bottomTitle: 'Bottom bar',
  sharedTitle: 'Both bars',
  showTopBar: 'Show top bar',
  hiddenTopNote: 'The top bar is off. Its messages stay here but are not sent to browser source.',
  messages: 'Messages',
  addMessage: 'Add message',
  noMessages: 'No messages yet. A bar with no messages keeps its size, color and logo on air and shows no text.',
  labelPlaceholder: 'Label (optional)',
  textPlaceholder: 'Message text',
  barColor: 'Bar color',
  anyColor: 'Any color',
  labelLook: 'Label look',
  messageLook: 'Message look',
  labelGap: 'Space between label and message',
  divider: 'Divider',
  bar: 'Bar',
  logo: 'Logo',
  showLogo: 'Show logo',
  logoNeedsImage: 'Pick an image to show a logo.',
  logoKeep: 'Keep',
  logoClear: 'Clear',
  logoForeignNote: 'This Message ticker belongs to another account: you can keep or clear its logo, not swap it.',
  logoForeignNone: 'This Message ticker belongs to another account, so only its owner can pick a logo.'
})

export const TICKER_BAR_DIVIDER_CHOICES: ReadonlyArray<{ mode: TickerBarBarStyle['dividerMode'], label: string }> = [
  { mode: 'AFTER_LABEL', label: 'On after the label' },
  { mode: 'AFTER_MESSAGE', label: 'On after the message' },
  { mode: 'OFF', label: 'Off' }
]

export const tickerBarMessageCountLabel = (count: number): string => `${count} of ${MAX_TICKER_BAR_MESSAGES}`

export const tickerBarCharsLeftLabel = (text: string): string => {
  const left = unitsLeft(text, MAX_TICKER_BAR_TEXT)
  return `${left} character${left === 1 ? '' : 's'} left`
}

export const LABEL_COLOR_CUSTOM = 'custom'
export const labelColorChoiceOf = (labelColor: string | null): string => {
  if (labelColor == null || labelColor === '') return ''
  const lower = labelColor.toLowerCase()
  const pick = TICKER_BAR_LABEL_COLOR_PICKS.find((p) => p.color === lower)
  return pick == null ? LABEL_COLOR_CUSTOM : pick.color
}

export const movedTickerBarMessages = (list: TickerBarMessageDraft[], index: number, delta: -1 | 1): TickerBarMessageDraft[] => {
  const to = index + delta
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list
  const next = [...list]
  next[index] = list[to]
  next[to] = list[index]
  return next
}

const WholeField = ({ label, value, bound, onChange, testId, help, disabled }: {
  label: string
  value: number
  bound: TickerBarNumberBound
  onChange: (next: number) => void
  testId: string
  help?: string
  disabled?: boolean
}): JSX.Element => (
  <label className='block'>
    <FieldLabel>{label}</FieldLabel>
    <NumberInput
      value={value}
      min={bound.min}
      max={bound.max}
      onCommit={onChange}
      disabled={disabled}
      aria-label={label}
      data-testid={testId}
      className={tickerBarInputClass}
    />
    {help != null && <p className='mt-1 text-[11px] text-dm-text-accent'>{help}</p>}
  </label>
)

const DecimalField = ({ label, value, bound, onChange, testId, help, allowEmpty = false, placeholder, disabled }: {
  label: string
  value: number | null
  bound: TickerBarNumberBound
  onChange: (next: number | null) => void
  testId: string
  help?: string
  allowEmpty?: boolean
  placeholder?: string
  disabled?: boolean
}): JSX.Element => {
  const shown = value == null ? '' : String(value)
  const [text, setText] = useState(shown)
  const [focused, setFocused] = useState(false)
  useEffect(() => {
    if (!focused) setText(shown)
  }, [shown, focused])
  const commit = (): void => {
    const trimmed = text.trim()
    if (trimmed === '') {
      if (allowEmpty) {
        onChange(null)
        return
      }
      setText(shown)
      return
    }
    const next = clampToBound(Number.parseFloat(trimmed), bound, value ?? bound.min)
    setText(String(next))
    onChange(next)
  }
  return (
    <label className='block'>
      <FieldLabel>{label}</FieldLabel>
      <input
        type='text'
        inputMode='decimal'
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        onChange={(e) => {
          if (/^\d*(?:\.\d{0,2})?$/.test(e.target.value)) setText(e.target.value)
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          commit()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
        }}
        className={tickerBarInputClass}
      />
      {help != null && <p className='mt-1 text-[11px] text-dm-text-accent'>{help}</p>}
    </label>
  )
}

const OPACITY_PERCENT_BOUND: TickerBarNumberBound = { min: 0, max: 100, decimals: 0 }

const SwitchRow = ({ label, checked, onChange, testId, disabled }: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
  testId: string
  disabled?: boolean
}): JSX.Element => (
  <label className='flex items-center gap-2 text-sm text-dm-text-primary'>
    <input
      type='checkbox'
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      data-testid={testId}
      className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
    />
    {label}
  </label>
)

const SolidColorField = ({ label, value, onChange }: {
  label: string
  value: string
  onChange: (next: string) => void
}): JSX.Element => <ColorInput label={label} value={value} onChange={onChange} solidOnly />

const AlphaColorField = ({ label, value, onChange, allowTransparent = false }: {
  label: string
  value: string
  onChange: (next: string) => void
  allowTransparent?: boolean
}): JSX.Element => (
  <ColorInput
    label={label}
    value={value}
    onChange={(next) => onChange(!allowTransparent && next === 'transparent' ? '#00000000' : next)}
  />
)

const useImageNaturalSize = (url: string | null): LogoNaturalSize | null => {
  const [size, setSize] = useState<{ url: string, natural: LogoNaturalSize } | null>(null)
  useEffect(() => {
    if (url == null || url === '' || typeof globalThis.Image === 'undefined') return
    let cancelled = false
    const img = new globalThis.Image()
    img.onload = () => {
      if (cancelled || !(img.naturalWidth > 0) || !(img.naturalHeight > 0)) return
      setSize({ url, natural: { width: img.naturalWidth, height: img.naturalHeight } })
    }
    img.src = url
    return () => {
      cancelled = true
      img.onload = null
    }
  }, [url])
  return size != null && size.url === url ? size.natural : null
}

const KeepOrClearLogo = ({ savedUrl, value, onChange, disabled }: {
  savedUrl: string | null
  value: string | null
  onChange: (next: string | null) => void
  disabled?: boolean
}): JSX.Element => {
  if (savedUrl == null) {
    return <p className='text-[11px] text-dm-text-accent' data-testid='ticker-logo-foreign-none'>{TICKER_BAR_CARD_COPY.logoForeignNone}</p>
  }
  const kept = value === savedUrl
  const tab = (on: boolean): string => on
    ? 'rounded bg-white/10 px-3 py-1 text-xs font-semibold text-dm-text-primary ring-1 ring-inset ring-white/20'
    : 'rounded px-3 py-1 text-xs font-medium text-dm-text-secondary hover:text-dm-text-primary'
  return (
    <div className='space-y-2' data-testid='ticker-logo-keep-or-clear'>
      <div className='flex items-center gap-3'>
        <div className='flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-white/10'>
          {kept ? <img src={savedUrl} alt='' className='size-full object-contain' /> : null}
        </div>
        <div className='inline-flex rounded-md border border-white/10 p-0.5' role='group' aria-label='Logo'>
          <button type='button' onClick={() => onChange(savedUrl)} aria-pressed={kept} disabled={disabled} data-testid='ticker-logo-keep' className={tab(kept)}>
            {TICKER_BAR_CARD_COPY.logoKeep}
          </button>
          <button type='button' onClick={() => onChange(null)} aria-pressed={!kept} disabled={disabled} data-testid='ticker-logo-clear' className={tab(!kept)}>
            {TICKER_BAR_CARD_COPY.logoClear}
          </button>
        </div>
      </div>
      <p className='text-[11px] text-dm-text-accent'>{TICKER_BAR_CARD_COPY.logoForeignNote}</p>
    </div>
  )
}

const MessageRow = ({ which, index, count, message, barLabelColor, problems, onChange, onMove, onRemove, disabled }: {
  which: 'top' | 'bottom'
  index: number
  count: number
  message: TickerBarMessageDraft
  barLabelColor: string
  problems: TickerBarDraftProblem[]
  onChange: (next: TickerBarMessageDraft) => void
  onMove: (delta: -1 | 1) => void
  onRemove: () => void
  disabled?: boolean
}): JSX.Element => {
  const choice = labelColorChoiceOf(message.labelColor)
  const swatch = message.labelColor != null && isHex6Color(message.labelColor) ? message.labelColor : barLabelColor
  const iconButton = 'rounded-md border border-white/10 p-1.5 text-dm-text-accent hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-40'
  const position = `${which === 'top' ? 'Top' : 'Bottom'} bar message ${index + 1}`
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3' data-testid={`ticker-${which}-message`}>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
        <input
          value={message.label}
          onChange={(e) => onChange({ ...message, label: clipToUnits(e.target.value, MAX_TICKER_BAR_LABEL) })}
          placeholder={TICKER_BAR_CARD_COPY.labelPlaceholder}
          aria-label={`${position} label`}
          disabled={disabled}
          data-testid={`ticker-${which}-label-${index}`}
          className={tickerBarInputClass}
        />
        <div className='flex items-center gap-2 sm:col-span-2'>
          <select
            value={choice}
            onChange={(e) => {
              const v = e.target.value
              if (v === '') onChange({ ...message, labelColor: null })
              else if (v === LABEL_COLOR_CUSTOM) onChange({ ...message, labelColor: swatch.toLowerCase() })
              else onChange({ ...message, labelColor: v })
            }}
            aria-label={`${position} label color`}
            disabled={disabled}
            data-testid={`ticker-${which}-label-color-${index}`}
            className={tickerBarInputClass}
          >
            <option value=''>{TICKER_BAR_CARD_COPY.barColor}</option>
            {TICKER_BAR_LABEL_COLOR_PICKS.map((p) => (
              <option key={p.color} value={p.color}>{p.label}</option>
            ))}
            <option value={LABEL_COLOR_CUSTOM}>{TICKER_BAR_CARD_COPY.anyColor}</option>
          </select>
          <input
            type='color'
            value={swatch.toLowerCase()}
            onChange={(e) => onChange({ ...message, labelColor: e.target.value.toLowerCase() })}
            aria-label={`${position} label color picker`}
            disabled={disabled}
            className='h-9 w-10 shrink-0 cursor-pointer rounded-md border border-white/10 bg-transparent'
          />
        </div>
        <div className='sm:col-span-3'>
          <input
            value={message.text}
            onChange={(e) => onChange({ ...message, text: clipToUnits(e.target.value, MAX_TICKER_BAR_TEXT) })}
            placeholder={TICKER_BAR_CARD_COPY.textPlaceholder}
            aria-label={`${position} text`}
            disabled={disabled}
            data-testid={`ticker-${which}-text-${index}`}
            className={tickerBarInputClass}
          />
          <div className='mt-1 flex items-center justify-between gap-2'>
            <span className='text-[11px] tabular-nums text-dm-text-accent' data-testid={`ticker-${which}-chars-left-${index}`}>
              {tickerBarCharsLeftLabel(message.text)}
            </span>
            <span className='flex items-center gap-1.5'>
              <button type='button' onClick={() => onMove(-1)} disabled={disabled === true || index === 0} title='Move up' className={iconButton}>
                <ArrowUpIcon className='size-3.5' aria-hidden='true' />
                <span className='sr-only'>Move {position} up</span>
              </button>
              <button type='button' onClick={() => onMove(1)} disabled={disabled === true || index === count - 1} title='Move down' className={iconButton}>
                <ArrowDownIcon className='size-3.5' aria-hidden='true' />
                <span className='sr-only'>Move {position} down</span>
              </button>
              <button type='button' onClick={onRemove} disabled={disabled} title='Delete' data-testid={`ticker-${which}-remove-${index}`} className='rounded-md border border-white/10 p-1.5 text-dm-text-accent hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40'>
                <TrashIcon className='size-3.5' aria-hidden='true' />
                <span className='sr-only'>Delete {position}</span>
              </button>
            </span>
          </div>
        </div>
      </div>
      {problems.length > 0 && (
        <ul className='mt-2 space-y-0.5 text-[11px] text-red-300' data-testid={`ticker-${which}-problems-${index}`}>
          {problems.map((p) => <li key={`${p.field}-${p.message}`}>{p.message}</li>)}
        </ul>
      )}
    </li>
  )
}

const LookFields = ({ title, testPrefix, fontKey, weight, size, color, onFont, onWeight, onSize, onColor, disabled }: {
  title: string
  testPrefix: string
  fontKey: string
  weight: number
  size: number
  color: string
  onFont: (key: string) => void
  onWeight: (w: number) => void
  onSize: (px: number) => void
  onColor: (c: string) => void
  disabled?: boolean
}): JSX.Element => (
  <div className='rounded-lg border border-white/5 p-3'>
    <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-dm-text-secondary'>{title}</h4>
    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
      <label className='block'>
        <FieldLabel>Font</FieldLabel>
        <FontSelect
          value={fontKey}
          onChange={onFont}
          disabled={disabled}
          aria-label={`${title} font`}
          data-testid={`${testPrefix}-font`}
          className={tickerBarInputClass}
        />
      </label>
      <FontWeightField
        label='Weight'
        testIdPrefix={`${testPrefix}-weight`}
        fontKey={fontKey}
        valueWeight={weight}
        roleDefault={WEIGHT_DEFAULT}
        onCommit={onWeight}
        className={tickerBarInputClass}
      />
      <WholeField
        label='Size (px)'
        value={size}
        bound={TICKER_BAR_BAR_BOUNDS.labelSizePx}
        onChange={onSize}
        testId={`${testPrefix}-size`}
        disabled={disabled}
      />
      <SolidColorField label='Color' value={color} onChange={onColor} />
    </div>
  </div>
)

export interface TickerBarStyleCardProps {
  which: 'top' | 'bottom'
  bar: TickerBarBarStyle
  messages: TickerBarMessageDraft[]
  showTopBar?: boolean
  onShowTopBarChange?: (on: boolean) => void
  onBarChange: (patch: Partial<TickerBarBarStyle>) => void
  onMessagesChange: (next: TickerBarMessageDraft[]) => void
  problems: TickerBarDraftProblem[]
  logoMode: 'library' | 'keepOrClear'
  savedLogoUrl: string | null
  disabled?: boolean
}

export const TickerBarStyleCard = ({
  which,
  bar,
  messages,
  showTopBar,
  onShowTopBarChange,
  onBarChange,
  onMessagesChange,
  problems,
  logoMode,
  savedLogoUrl,
  disabled
}: TickerBarStyleCardProps): JSX.Element => {
  const isTop = which === 'top'
  const natural = useImageNaturalSize(bar.logoOn ? bar.logoUrl : null)
  const logoShrunk = tickerBarLogoBoxOf(bar, natural, TICKER_BAR_MARQUEE_WIDTH)?.shrunk === true
  const textTaller = tickerBarTextTallerThanRow(bar)
  const countProblems = problems.filter((p) => p.index == null)
  const atCap = messages.length >= MAX_TICKER_BAR_MESSAGES
  const prefix = `ticker-${which}`

  const setMessage = (index: number, next: TickerBarMessageDraft): void =>
    onMessagesChange(messages.map((m, i) => (i === index ? next : m)))

  const fontChange = (role: 'label' | 'text') => (key: string): void => {
    if (role === 'label') onBarChange({ labelFontKey: key, labelFontWeight: effectiveChatFontWeight(key, bar.labelFontWeight, WEIGHT_DEFAULT) })
    else onBarChange({ textFontKey: key, textFontWeight: effectiveChatFontWeight(key, bar.textFontWeight, WEIGHT_DEFAULT) })
  }

  return (
    <SectionCard title={isTop ? TICKER_BAR_CARD_COPY.topTitle : TICKER_BAR_CARD_COPY.bottomTitle}>
      <div className='space-y-4' data-testid={`${prefix}-card`}>
        {isTop && onShowTopBarChange != null && (
          <div>
            <SwitchRow label={TICKER_BAR_CARD_COPY.showTopBar} checked={showTopBar === true} onChange={onShowTopBarChange} testId='ticker-show-top-bar' disabled={disabled} />
            {showTopBar !== true && (
              <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='ticker-top-hidden-note'>{TICKER_BAR_CARD_COPY.hiddenTopNote}</p>
            )}
          </div>
        )}

        <div>
          <div className='mb-2 flex items-center justify-between gap-2'>
            <FieldLabel>{TICKER_BAR_CARD_COPY.messages}</FieldLabel>
            <span className='text-[11px] tabular-nums text-dm-text-accent' data-testid={`${prefix}-count`}>
              {tickerBarMessageCountLabel(messages.length)}
            </span>
          </div>
          {messages.length === 0
            ? <p className='mb-2 text-[11px] text-dm-text-accent'>{TICKER_BAR_CARD_COPY.noMessages}</p>
            : (
              <ul className='mb-2 space-y-2'>
                {messages.map((m, i) => (
                  <MessageRow
                    key={m.key}
                    which={which}
                    index={i}
                    count={messages.length}
                    message={m}
                    barLabelColor={bar.labelColor}
                    problems={problems.filter((p) => p.index === i)}
                    onChange={(next) => setMessage(i, next)}
                    onMove={(delta) => onMessagesChange(movedTickerBarMessages(messages, i, delta))}
                    onRemove={() => onMessagesChange(messages.filter((_, j) => j !== i))}
                    disabled={disabled}
                  />
                ))}
              </ul>
              )}
          {countProblems.length > 0 && (
            <ul className='mb-2 space-y-0.5 text-[11px] text-red-300'>
              {countProblems.map((p) => <li key={`${p.field}-${p.message}`}>{p.message}</li>)}
            </ul>
          )}
          <button
            type='button'
            onClick={() => onMessagesChange([...messages, emptyTickerBarMessage()])}
            disabled={disabled === true || atCap}
            data-testid={`${prefix}-add-message`}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PlusIcon className='size-3.5' aria-hidden='true' />
            {TICKER_BAR_CARD_COPY.addMessage}
          </button>
        </div>

        <div className='grid grid-cols-1 gap-3 xl:grid-cols-2'>
          <LookFields
            title={TICKER_BAR_CARD_COPY.labelLook}
            testPrefix={`${prefix}-label`}
            fontKey={bar.labelFontKey}
            weight={bar.labelFontWeight}
            size={bar.labelSizePx}
            color={bar.labelColor}
            onFont={fontChange('label')}
            onWeight={(w) => onBarChange({ labelFontWeight: w })}
            onSize={(px) => onBarChange({ labelSizePx: px })}
            onColor={(c) => onBarChange({ labelColor: c })}
            disabled={disabled}
          />
          <LookFields
            title={TICKER_BAR_CARD_COPY.messageLook}
            testPrefix={`${prefix}-text`}
            fontKey={bar.textFontKey}
            weight={bar.textFontWeight}
            size={bar.textSizePx}
            color={bar.textColor}
            onFont={fontChange('text')}
            onWeight={(w) => onBarChange({ textFontWeight: w })}
            onSize={(px) => onBarChange({ textSizePx: px })}
            onColor={(c) => onBarChange({ textColor: c })}
            disabled={disabled}
          />
        </div>
        {textTaller && (
          <p className='text-[11px] text-amber-300' data-testid={`${prefix}-text-taller`}>{TICKER_BAR_COPY.textTaller}</p>
        )}

        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <WholeField
            label={TICKER_BAR_CARD_COPY.labelGap}
            value={bar.labelGapPx}
            bound={TICKER_BAR_BAR_BOUNDS.labelGapPx}
            onChange={(n) => onBarChange({ labelGapPx: n })}
            testId={`${prefix}-label-gap`}
            help='Used when no divider sits between the label and the message.'
            disabled={disabled}
          />
          <label className='block'>
            <FieldLabel>{TICKER_BAR_CARD_COPY.divider}</FieldLabel>
            <select
              value={bar.dividerMode}
              onChange={(e) => onBarChange({ dividerMode: e.target.value })}
              disabled={disabled}
              aria-label={`${isTop ? 'Top' : 'Bottom'} bar divider`}
              data-testid={`${prefix}-divider`}
              className={tickerBarInputClass}
            >
              {TICKER_BAR_DIVIDER_CHOICES.map((c) => (
                <option key={c.mode} value={c.mode}>{c.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className='rounded-lg border border-white/5 p-3'>
          <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-dm-text-secondary'>{TICKER_BAR_CARD_COPY.bar}</h4>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <SolidColorField label='Background' value={bar.bgColor} onChange={(c) => onBarChange({ bgColor: c })} />
            <WholeField
              label='Opacity (%)'
              value={Math.round(bar.opacity * 100)}
              bound={OPACITY_PERCENT_BOUND}
              onChange={(n) => onBarChange({ opacity: n / 100 })}
              testId={`${prefix}-opacity`}
              help='100 is solid; lower lets the scene show through.'
              disabled={disabled}
            />
            <WholeField
              label='Row height (px)'
              value={bar.lineHeightPx}
              bound={TICKER_BAR_BAR_BOUNDS.lineHeightPx}
              onChange={(n) => onBarChange({ lineHeightPx: n })}
              testId={`${prefix}-row-height`}
              disabled={disabled}
            />
            <DecimalField
              label='Top padding (px)'
              value={bar.padTop}
              bound={TICKER_BAR_BAR_BOUNDS.padTop}
              onChange={(n) => onBarChange({ padTop: n ?? bar.padTop })}
              testId={`${prefix}-pad-top`}
              disabled={disabled}
            />
            <DecimalField
              label='Bottom padding (px)'
              value={bar.padBottom}
              bound={TICKER_BAR_BAR_BOUNDS.padBottom}
              onChange={(n) => onBarChange({ padBottom: n ?? bar.padBottom })}
              testId={`${prefix}-pad-bottom`}
              disabled={disabled}
            />
            <DecimalField
              label='Outer padding (px)'
              value={bar.outerPad}
              bound={TICKER_BAR_BAR_BOUNDS.outerPad}
              onChange={(n) => onBarChange({ outerPad: n ?? bar.outerPad })}
              testId={`${prefix}-outer-pad`}
              help={isTop ? 'Space above the top bar.' : 'Space below the bottom bar.'}
              disabled={disabled}
            />
            <DecimalField
              label='Space between messages (px)'
              value={bar.separatorGapPx}
              bound={TICKER_BAR_BAR_BOUNDS.separatorGapPx}
              onChange={(n) => onBarChange({ separatorGapPx: n })}
              testId={`${prefix}-separator-gap`}
              allowEmpty
              placeholder='Auto'
              help='Empty means automatic: one and a half times the message size.'
              disabled={disabled}
            />
            <label className='block'>
              <FieldLabel>Scroll direction</FieldLabel>
              <select
                value={bar.scrollReverse ? 'reverse' : 'normal'}
                onChange={(e) => onBarChange({ scrollReverse: e.target.value === 'reverse' })}
                disabled={disabled}
                aria-label={`${isTop ? 'Top' : 'Bottom'} bar scroll direction`}
                data-testid={`${prefix}-direction`}
                className={tickerBarInputClass}
              >
                <option value='normal'>Right to left</option>
                <option value='reverse'>Left to right</option>
              </select>
            </label>
          </div>
        </div>

        <div className='rounded-lg border border-white/5 p-3'>
          <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-dm-text-secondary'>{TICKER_BAR_CARD_COPY.logo}</h4>
          <div className='space-y-3'>
            <SwitchRow label={TICKER_BAR_CARD_COPY.showLogo} checked={bar.logoOn} onChange={(on) => onBarChange({ logoOn: on })} testId={`${prefix}-logo-on`} disabled={disabled} />
            {logoMode === 'library'
              ? (
                <ImageUpload
                  value={bar.logoUrl ?? ''}
                  onChange={(next) => onBarChange({ logoUrl: next === '' ? null : next })}
                  label='Logo image'
                  helpText='From your StagePilot library: PNG, JPEG, WebP or GIF up to 8 MB.'
                />
                )
              : <KeepOrClearLogo savedUrl={savedLogoUrl} value={bar.logoUrl} onChange={(next) => onBarChange({ logoUrl: next })} disabled={disabled} />}
            {bar.logoOn && bar.logoUrl == null && (
              <p className='text-[11px] text-amber-300' data-testid={`${prefix}-logo-needs-image`}>{TICKER_BAR_CARD_COPY.logoNeedsImage}</p>
            )}
            {logoShrunk && (
              <p className='text-[11px] text-amber-300' data-testid={`${prefix}-logo-shrunk`}>{TICKER_BAR_COPY.logoShrunk}</p>
            )}
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
              <WholeField
                label='Logo size (px)'
                value={bar.logoHeightPx}
                bound={TICKER_BAR_BAR_BOUNDS.logoHeightPx}
                onChange={(n) => onBarChange({ logoHeightPx: n })}
                testId={`${prefix}-logo-size`}
                help='Never taller than the bar.'
                disabled={disabled}
              />
              <WholeField
                label='Space between text and logo (px)'
                value={bar.logoGapPx}
                bound={TICKER_BAR_BAR_BOUNDS.logoGapPx}
                onChange={(n) => onBarChange({ logoGapPx: n })}
                testId={`${prefix}-logo-gap`}
                disabled={disabled}
              />
              <WholeField
                label='Space after logo (px)'
                value={bar.logoEdgePadPx}
                bound={TICKER_BAR_BAR_BOUNDS.logoEdgePadPx}
                onChange={(n) => onBarChange({ logoEdgePadPx: n })}
                testId={`${prefix}-logo-edge`}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  )
}

export const TickerBarSharedCard = ({ style, onChange, disabled }: {
  style: TickerBarStyle
  onChange: (patch: Partial<Omit<TickerBarStyle, 'top' | 'bottom'>>) => void
  disabled?: boolean
}): JSX.Element => {
  const pageTransparent = style.pageBackground === 'transparent'
  return (
    <SectionCard title={TICKER_BAR_CARD_COPY.sharedTitle}>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2' data-testid='ticker-shared-card'>
        <WholeField
          label='Scroll speed (px per second)'
          value={style.scrollSpeedPxS}
          bound={TICKER_BAR_SHARED_BOUNDS.scrollSpeedPxS}
          onChange={(n) => onChange({ scrollSpeedPxS: n })}
          testId='ticker-speed'
          help='One speed for both bars.'
          disabled={disabled}
        />
        <WholeField
          label='Gap between bars (px)'
          value={style.gapHeight}
          bound={TICKER_BAR_SHARED_BOUNDS.gapHeight}
          onChange={(n) => onChange({ gapHeight: n })}
          testId='ticker-gap-height'
          disabled={disabled}
        />
        <AlphaColorField label='Gap color' value={style.gapColor} onChange={(c) => onChange({ gapColor: c })} allowTransparent />
        <AlphaColorField label='Border color' value={style.borderColor} onChange={(c) => onChange({ borderColor: c })} />
        <WholeField
          label='Border width (px)'
          value={style.borderWidth}
          bound={TICKER_BAR_SHARED_BOUNDS.borderWidth}
          onChange={(n) => onChange({ borderWidth: n })}
          testId='ticker-border-width'
          disabled={disabled}
        />
        <WholeField
          label='Glow size (px)'
          value={style.textGlowPx}
          bound={TICKER_BAR_SHARED_BOUNDS.textGlowPx}
          onChange={(n) => onChange({ textGlowPx: n })}
          testId='ticker-glow-size'
          help='0 is off.'
          disabled={disabled}
        />
        <AlphaColorField label='Glow color' value={style.textGlowColor} onChange={(c) => onChange({ textGlowColor: c })} />
        <div className='block'>
          <FieldLabel>Page background</FieldLabel>
          <select
            value={pageTransparent ? 'transparent' : 'color'}
            onChange={(e) => onChange({ pageBackground: e.target.value === 'transparent' ? 'transparent' : '#000000' })}
            disabled={disabled}
            aria-label='Page background'
            data-testid='ticker-page-background'
            className={tickerBarInputClass}
          >
            <option value='transparent'>Transparent</option>
            <option value='color'>A color</option>
          </select>
          {!pageTransparent && (
            <div className='mt-2'>
              <ColorInput value={style.pageBackground} onChange={(c) => onChange({ pageBackground: c === 'transparent' ? 'transparent' : c })} solidOnly inline />
            </div>
          )}
          <p className='mt-1 text-[11px] text-dm-text-accent'>Only the Message ticker&apos;s own link paints it. Scenes are always transparent.</p>
        </div>
      </div>
    </SectionCard>
  )
}
